import { Site } from '../../core/Site/Site';
import { Destination } from '../../resources/destination/domain/types/destination';
import { Itinerary, TOURISM_TYPE_NO_PLANE } from '../../resources/itinerary/domain/types/Itinerary';
import { useCurrentSite } from '../providers/currentSite/useCurrentSite';
import { DESTINATION_ID } from '../utilities/destinationId';
import { PickDomain } from '../utilities/types/PickDomain';

const RAIL_EUROPE_DESTINATIONS_IDS = [
    DESTINATION_ID.AUSTRIA,
    DESTINATION_ID.BALTIC_STATES,
    DESTINATION_ID.BULGARIA,
    DESTINATION_ID.CROATIA,
    DESTINATION_ID.DENMARK,
    DESTINATION_ID.ENGLAND,
    DESTINATION_ID.ESTONIA,
    DESTINATION_ID.FRANCE,
    DESTINATION_ID.GERMANY,
    DESTINATION_ID.HUNGARY,
    DESTINATION_ID.ITALY,
    DESTINATION_ID.LATVIA,
    DESTINATION_ID.LITHUANIA,
    DESTINATION_ID.MACEDONIA,
    DESTINATION_ID.MONTENEGRO,
    DESTINATION_ID.NORWAY,
    DESTINATION_ID.POLAND,
    DESTINATION_ID.PORTUGAL,
    DESTINATION_ID.ROMANIA,
    DESTINATION_ID.SCOTLAND,
    DESTINATION_ID.SLOVAKIA,
    DESTINATION_ID.SLOVENIA,
    DESTINATION_ID.SPAIN,
    DESTINATION_ID.SWEDEN,
    DESTINATION_ID.SWITZERLAND,
    DESTINATION_ID.TURKEY,
    DESTINATION_ID.UKRAINE,
    DESTINATION_ID.UNITED_KINGDOM,
    DESTINATION_ID.WALES,
];

const OPTION_WAY_SUPPORTED_LANGUAGES = ['fr', 'en', 'es', 'de', 'it'];

function isRailEuropeAvailableForDestination(destinationId: number) {
    return RAIL_EUROPE_DESTINATIONS_IDS.includes(destinationId);
}

function isRailEuropeAvailableForCurrentSiteAndDestination(
    currentSite: Site,
    destinationId: number,
) {
    if (currentSite.locale !== 'fr-FR') {
        return true;
    }

    if (destinationId !== DESTINATION_ID.FRANCE) {
        return true;
    }

    return false;
}

function getOptionWayLanguage(currentSite: Site): string {
    const lang = currentSite.locale.split('-')[0].toLowerCase();
    if (!OPTION_WAY_SUPPORTED_LANGUAGES.includes(lang)) {
        return 'en';
    }
    return lang;
}

type TransportsInput = {
    itinerary?: PickDomain<Itinerary, 'tourismTypes'>;
    destination: PickDomain<
        Destination,
        'id',
        {
            parentDestination: Pick<Destination, 'id'> | null;
        }
    >;
};

export function useTransports(input: TransportsInput) {
    const { currentSite } = useCurrentSite();

    return getTransports({ ...input, currentSite });
}

/**
 * The hook's decision, without the hook — a server render needs it too, to know whether the
 * "how to go" content belongs in the page's JSON-LD.
 */
export function getTransports({
    itinerary,
    destination,
    currentSite,
}: TransportsInput & { currentSite: Site }) {
    const countryDestinationId = destination.parentDestination?.id ?? destination.id;

    const isRailEuropeAvailableForCurrentSite = currentSite.railEuropeUrl !== null;
    const showRailEurope =
        isRailEuropeAvailableForCurrentSite &&
        isRailEuropeAvailableForDestination(countryDestinationId) &&
        isRailEuropeAvailableForCurrentSiteAndDestination(currentSite, countryDestinationId);

    const hasNoPlaneTourismType =
        itinerary?.tourismTypes.find((tourismType) => tourismType === TOURISM_TYPE_NO_PLANE) ??
        false;
    const showOptionWay =
        currentSite.optionWayUrl !== null &&
        !hasNoPlaneTourismType &&
        currentSite.marketDestinationId !== countryDestinationId;

    const optionWayLanguage = getOptionWayLanguage(currentSite);

    return {
        showRailEurope,
        showOptionWay,
        optionWayLanguage,
        railEuropeUrl: currentSite.railEuropeUrl,
    };
}
